import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { MonthCalendar } from "@/components/MonthCalendar";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { formatDateIT, monthKey, todayISO } from "@/lib/dates";

export default function EmployeeHolidays() {
  const { user } = useAuth();
  const [cursor, setCursor] = useState(new Date());
  const [holidays, setHolidays] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [startDate, setStartDate] = useState(todayISO());
  const [endDate, setEndDate] = useState(todayISO());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const res = await api.get(`/holidays?month=${monthKey(cursor)}`);
    setHolidays(res.data);
  };

  useEffect(() => {
    load().catch(() => toast.error("Errore nel caricamento"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor]);

  const submit = async () => {
    if (endDate < startDate) return toast.error("La data di fine deve essere dopo l'inizio");
    setSaving(true);
    try {
      await api.post("/holidays", { start_date: startDate, end_date: endDate });
      toast.success("Richiesta ferie inviata!");
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const move = (delta) => {
    const d = new Date(cursor);
    d.setMonth(d.getMonth() + delta);
    setCursor(d);
  };

  const mine = holidays.filter((h) => h.employee_id === user.id);
  const others = holidays.filter((h) => h.employee_id !== user.id);

  return (
    <div className="space-y-5">
      <h1 className="font-head text-2xl font-bold text-slate-900">Ferie</h1>

      <MonthCalendar date={cursor} onPrev={() => move(-1)} onNext={() => move(1)} holidays={holidays} />

      {!showForm ? (
        <button
          data-testid="new-holiday-button"
          onClick={() => setShowForm(true)}
          className="w-full h-14 rounded-xl bg-primary text-primary-foreground text-lg font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
        >
          <Plus className="h-5 w-5" /> Nuova richiesta ferie
        </button>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
          <h2 className="font-head text-lg font-bold text-slate-900">Richiedi ferie</h2>
          <div>
            <label className="text-sm font-semibold text-slate-600 mb-1.5 block">Dal</label>
            <input
              data-testid="holiday-start"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full h-14 rounded-xl border border-slate-200 px-4 text-lg outline-none focus:border-primary"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-600 mb-1.5 block">Al</label>
            <input
              data-testid="holiday-end"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full h-14 rounded-xl border border-slate-200 px-4 text-lg outline-none focus:border-primary"
            />
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowForm(false)}
              className="flex-1 h-14 rounded-xl border border-slate-200 font-bold text-slate-600 active:scale-[0.98]"
            >
              Annulla
            </button>
            <button
              data-testid="submit-holiday-button"
              onClick={submit}
              disabled={saving}
              className="flex-1 h-14 rounded-xl bg-primary text-primary-foreground font-bold flex items-center justify-center active:scale-[0.98] disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Invia"}
            </button>
          </div>
        </div>
      )}

      <div>
        <h2 className="font-head text-lg font-bold text-slate-900 mb-2">Le mie richieste</h2>
        <div className="space-y-2">
          {mine.length === 0 && <p className="text-slate-400 text-sm">Nessuna richiesta questo mese.</p>}
          {mine.map((h) => (
            <div key={h.id} data-testid="my-holiday" className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between">
              <span className="font-semibold text-slate-900">
                {formatDateIT(h.start_date)} → {formatDateIT(h.end_date)}
              </span>
              <StatusBadge status={h.status} />
            </div>
          ))}
        </div>
      </div>

      {others.length > 0 && (
        <div>
          <h2 className="font-head text-lg font-bold text-slate-900 mb-2">Ferie dei colleghi</h2>
          <div className="space-y-2">
            {others.map((h) => (
              <div key={h.id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="font-semibold text-slate-900">{h.employee_name}</p>
                  <p className="text-sm text-slate-500">{formatDateIT(h.start_date)} → {formatDateIT(h.end_date)}</p>
                </div>
                <StatusBadge status={h.status} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
